import React, { useState } from 'react';

import './styles.scss';

interface TabItem {
    label: string;
    content: React.ReactNode;
    selected?: boolean;
    isDisabled?: boolean;
}

interface TabProps {
    items: TabItem[];
    onTabChange?: (index: number) => void;
}

const TabNavItem: React.FC<{
    isActive: boolean;
    isDisabled: boolean;
    label: string;
    onClick: () => void;
}> = ({ isActive, isDisabled, label, onClick }) => {
    return (
        <div className='c-tabs__nav-item' onClick={onClick}>
            <button
                role='tab'
                type='button'
                id={`tab-${label}`}
                className={'c-tabs__nav-link'}
                aria-selected={isActive}
                aria-disabled={isDisabled}
                aria-controls={`tabpanel-${label}`}
            >
                <span className='c-tabs__text'>{label}</span>
            </button>
        </div>
    );
};

const TabContent: React.FC<{
    isActive: boolean;
    content: React.ReactNode;
    label: string;
}> = ({ isActive, content, label }) => {
    return (
        <div
            role='tabpanel'
            id={`panel-${label}`}
            className={`c-tabs__content ${isActive ? 'active' : ''}`}
            aria-hidden={!isActive}
            aria-labelledby={`tab-${label}`}
        >
            {content}
        </div>
    );
};

const Tab: React.FC<TabProps> = ({ items, onTabChange }) => {
    const DEFAULT_TAB_INDEX = 0;
    const [activeTab, setActiveTab] = useState<number>(
        items.findIndex((item) => item.selected) || DEFAULT_TAB_INDEX,
    );

    const handleTabClick = (index: number) => {
        if (!items[index].isDisabled) {
            setActiveTab(index);
            onTabChange?.(index);
        }
    };
    return (
        <div className='c-tabs js-tabs c-tabs--no-overrides c-tabs--credit-status'>
            <div className='c-tabs__nav-container'>
                <div className='c-tabs__nav' role='tablist'>
                    {items.map((item, index) => (
                        <TabNavItem
                            key={index}
                            isActive={activeTab === index}
                            isDisabled={item.isDisabled ?? false}
                            label={item.label}
                            onClick={() => handleTabClick(index)}
                        />
                    ))}
                </div>
            </div>
            <div className='c-tabs__content-wrapper'>
                {items.map((item, index) => (
                    <TabContent
                        key={index}
                        isActive={activeTab === index}
                        content={item.content}
                        label={item.label}
                    />
                ))}
            </div>
        </div>
    );
};

export default Tab;
